import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createImages, createPowerPoint, downloadData, getImages, getPowerPoint, getPowerPointStatus, CreatePowerPointReq } from "./api";
import { StudyType } from "../studies/types/study.type";

/*
 * useCreatePowerPoint Mutation
 *
 * This mutation is used to create a PowerPoint presentation for a study.
 */
export const useCreatePowerPoint = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreatePowerPointReq) => createPowerPoint(req),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["ppt", vars.projectId, vars.studyId] });
    },
  });
};

/*
 * useGetPowerPointStatus Query
 *
 * This query is used to get the status of a PowerPoint presentation.
 * The endpoint is actually a post request but we are using a query here to allow for automatic polling.
 */
export const useGetPowerPointStatus = (projectId: string, studyId: string, taskId: string, intervalTiming = 1000) => {
  return useQuery({
    queryKey: ["pptSatus", taskId],
    queryFn: () => getPowerPointStatus(projectId, studyId, taskId),
    refetchInterval: (query) => (query?.state?.data?.status === "RUNNING" ? intervalTiming : false),
  });
};

/*
 * useGetPowerPoint Query
 *
 * This query is used to get the PowerPoint presentation.
 */
export const useGetPowerPoint = (projectId: string, studyId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["ppt", projectId, studyId],
    queryFn: () => getPowerPoint(projectId, studyId),
    enabled: enabled,
  });
};

/*
 * useCreateImages Mutation
 *
 * Initiates a visuals export for a study and invalidates the cached download on success.
 */
export const useCreateImages = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, studyId, type }: { projectId: string; studyId: string; type: StudyType }) => createImages(projectId, studyId, type),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["images", vars.projectId, vars.studyId] });
    },
  });
};

/*
 * useGetImagesStatus Query
 *
 * Polls the shared export status endpoint until the visuals export is no longer RUNNING.
 */
export const useGetImagesStatus = (projectId: string, studyId: string, taskId: string, intervalTiming = 1000) => {
  return useQuery({
    queryKey: ["imagesStatus", taskId],
    queryFn: () => getPowerPointStatus(projectId, studyId, taskId),
    refetchInterval: (query) => (query?.state?.data?.status === "RUNNING" ? intervalTiming : false),
  });
};

/*
 * useGetImages Query
 *
 * Fetches the pre-signed download URL for the visuals export once generation has succeeded.
 */
export const useGetImages = (projectId: string, studyId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["images", projectId, studyId],
    queryFn: () => getImages(projectId, studyId),
    enabled,
  });
};

/*
 * useGetData Query
 *
 * Fetches the study data (CSV) export directly. Unlike PowerPoint/visuals there is no
 * generation step to poll — the endpoint returns the file inline, so this resolves in a
 * single request. Caching is disabled so each open produces a fresh download.
 */
export const useGetData = (projectId: string, studyId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["data", projectId, studyId],
    queryFn: () => downloadData(projectId, studyId),
    enabled,
    gcTime: 0,
    staleTime: 0,
  });
};
